import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SuccessResponse, User } from "../types/generated";
import { apiService } from "../services/api";
import { Loader2 } from "lucide-react";

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute = ({ children }: AdminRouteProps) => {
  const location = useLocation();

  const {
    data: userResponse,
    isLoading,
    isError,
    error,
  } = useQuery<SuccessResponse<User>, Error>({
    queryKey: ["current-user"],
    queryFn: () => apiService.getCurrentUser(),
    retry: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Handle error state (including no token)
  if (isError) {
    console.error("Admin route error:", error);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user is admin and active
  const user = userResponse?.data;
  if (!user?.admin || !user?.active) {
    console.warn("Access denied: User is not an admin or is inactive");
    return <Navigate to="/dashboard" replace />;
  }

  // Render children if all checks pass
  return <>{children}</>;
};

export default AdminRoute;
